import { EMULATOR_TYPES } from '@localstack/constants';
import { EmulatorType, ProductFeature, UseAuthProviderReturnType } from '@localstack/types';

type Props = {
  useAuthProvider: () => UseAuthProviderReturnType;
};

const ENTITLING_FEATURES: Record<EmulatorType, [ProductFeature, string?]> = {
  // AWS has no feature of its own, so a service only it provides stands in for one.
  [EmulatorType.AWS]: [ProductFeature.FEATURE_LOCALSTACK_AWS_PROVIDER, '/^lambda:.+$/'],
  [EmulatorType.SNOWFLAKE]: [ProductFeature.FEATURE_SNOWFLAKE],
  [EmulatorType.AZURE]: [ProductFeature.FEATURE_AZURE_PREVIEW],
};

/**
 * Every emulator the organization is entitled to, in the order they should be presented.
 * A single entry means an upgrade has an unambiguous target, so callers can preselect that
 * product on the pricing page; several mean the intent is unknown and the choice belongs to
 * the user. Falls back to AWS, so a workspace we cannot place still has a product to show.
 */
export const useEntitledEmulators = ({ useAuthProvider }: Props): EmulatorType[] => {
  const { hasFeature } = useAuthProvider();

  const entitled = EMULATOR_TYPES.filter((emulator) => {
    const [feature, variant] = ENTITLING_FEATURES[emulator];
    return hasFeature(feature, variant);
  });

  return entitled.length > 0 ? entitled : [EmulatorType.AWS];
};
